import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Button,
    CircularProgress, Chip, TextField,
    InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Menu, MenuItem,
    Tooltip, Dialog, DialogTitle, DialogActions,
    DialogContent, TablePagination,
} from '@mui/material';
import {
    ShoppingBag as ShoppingBagIcon,
    Search as SearchIcon,
    Inventory2 as EmptyIcon,
    AccessTime as PendingIcon,
    Autorenew as ProcessingIcon,
    LocalShipping as ShippedIcon,
    CheckCircle as DeliveredIcon,
    Cancel as CancelledIcon,
    Visibility as ViewIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../services/api';
import '../styles/ProtectedRoutes.css';

// ── Status config ──────────────────────────────────────────────────────────────
export const STATUS_CONFIG = {
    Pending: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)', Icon: PendingIcon },
    Processing: { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.35)', Icon: ProcessingIcon },
    Shipped: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.35)', Icon: ShippedIcon },
    Delivered: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)', Icon: DeliveredIcon },
    Cancelled: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.35)', Icon: CancelledIcon },
};

export const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pending;
    const { Icon } = cfg;
    return (
        <Chip
            icon={<Icon sx={{ fontSize: '12px !important', color: `${cfg.color} !important` }} />}
            label={status}
            size="small"
            sx={{
                height: 20, fontSize: { xs: '0.62rem', sm: '0.7rem' }, fontWeight: 700,
                backgroundColor: cfg.bg,
                border: `1px solid ${cfg.border}`,
                color: cfg.color,
                '& .MuiChip-icon': { ml: '3px' },
                '& .MuiChip-label': { px: { xs: 0.6, sm: 1 } },
            }}
        />
    );
};

// ── Actions Menu ───────────────────────────────────────────────────────────────
const ActionsMenu = ({ order, onView }) => (
    <Tooltip title="View Details">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(order); }} sx={{
            color: 'rgba(188,150,94,0.7)', border: '1px solid rgba(188,150,94,0.2)',
            borderRadius: 1.5, width: 30, height: 30,
            '&:hover': { background: 'rgba(188,150,94,0.1)', color: '#bc965e', borderColor: 'rgba(188,150,94,0.5)' },
        }}>
            <ViewIcon sx={{ fontSize: 16 }} />
        </IconButton>
    </Tooltip>
);

// ── Table Header Cell ──────────────────────────────────────────────────────────
// TH component — note: I'm removing individual `width` and centralizing it below
const TH = ({ children, align = 'left' }) => (
    <TableCell align={align} sx={{
        color: '#ffffff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.72rem' },
        textTransform: 'uppercase', letterSpacing: '0.06em',
        borderBottom: '1px solid rgba(188,150,94,0.15)',
        background: 'rgba(188,150,94,0.04)', py: 1.5, px: { xs: 1, sm: 2 }, whiteSpace: 'nowrap',
    }}>
        {children}
    </TableCell>
);

const TD = ({ children, align = 'left', sx: sxProp = {} }) => (
    <TableCell align={align} sx={{
        color: 'rgba(255,255,255,0.94)', fontSize: { xs: '0.72rem', sm: '0.82rem' },
        borderBottom: '1px solid rgba(188,150,94,0.07)',
        py: 1.25, px: { xs: 1, sm: 2 },
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        ...sxProp,
    }}>
        {children}
    </TableCell>
);


// ── Main Component ─────────────────────────────────────────────────────────────
const CustomerOrderList = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
   

    // ── Pagination state ──────────────────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();
    const location = useLocation();
    const newBillNumber = location.state?.billNumber || null;

    useEffect(() => { fetchOrders(); }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const { data } = await API.get('/orders');
            if (data.success) setOrders(data.data);
        } catch {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const filtered = orders.filter(o => {
        const matchSearch = search
            ? String(o.BillId).toLowerCase().includes(search.toLowerCase())
            : true;
        const matchStatus = statusFilter === 'All'
            ? true
            : (o.OrderStatus || 'Pending') === statusFilter;
        return matchSearch && matchStatus;
    });

    // ── Reset to page 0 when filter/search changes ────────────────────────────
    useEffect(() => { setPage(0); }, [search, statusFilter]);

    // ── Slice filtered list for current page ──────────────────────────────────
    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const stats = Object.keys(STATUS_CONFIG).map(s => ({
        label: s,
        count: orders.filter(o => (o.OrderStatus || 'Pending') === s).length,
        ...STATUS_CONFIG[s],
    }));

    const handleView = (order) =>
        navigate(`/orders/${order.BillId}`, { state: { order } });


    if (loading) return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                <CircularProgress size={40} className="glass-loading" />
                <Typography variant="body2" className="glass-text-secondary">Loading your orders…</Typography>
            </Box>
        </Container>
    );

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 1.5, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>

            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }} className="glass-content-container">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.75 }}>
                    <ShoppingBagIcon sx={{ color: '#bc965e', fontSize: { xs: 22, sm: 28 } }} />
                    <Typography variant="h4" className="glass-title" sx={{ fontSize: { xs: '1.2rem', sm: '1.6rem', md: '2rem' } }}>
                        My Orders
                    </Typography>
                </Box>
                <Typography variant="body2" className="glass-text-secondary" sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' } }}>
                    {orders.length} order{orders.length !== 1 ? 's' : ''} placed
                </Typography>
            </Box>

            {/* Status filter pills */}
            {orders.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: { xs: 2, sm: 2.5 }, overflowX: 'auto', pb: 0.5, '::-webkit-scrollbar': { display: 'none' } }}>
                    {['All', ...Object.keys(STATUS_CONFIG)].map(s => {
                        const cfg = STATUS_CONFIG[s];
                        const count = s === 'All' ? orders.length : stats.find(x => x.label === s)?.count ?? 0;
                        if (s !== 'All' && count === 0) return null;
                        const active = statusFilter === s;
                        return (
                            <Chip key={s} label={`${s} (${count})`}
                                onClick={() => setStatusFilter(active ? 'All' : s)}
                                size="small"
                                sx={{
                                    height: 26, fontSize: '0.72rem', fontWeight: 700,
                                    cursor: 'pointer', flexShrink: 0,
                                    backgroundColor: active ? (cfg ? cfg.bg : 'rgba(188,150,94,0.2)') : 'rgba(188,150,94,0.05)',
                                    border: `1px solid ${active ? (cfg ? cfg.border : 'rgba(188,150,94,0.6)') : 'rgba(188,150,94,0.15)'}`,
                                    color: active ? (cfg ? cfg.color : '#bc965e') : 'rgba(255,255,255,0.45)',
                                    transition: 'all 0.15s',
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Search */}
            {orders.length > 0 && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <TextField fullWidth size="small" placeholder="Search by order number…"
                        value={search} onChange={e => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(216,210,200,0.84)', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                                sx: { color: 'white', fontSize: { xs: '0.82rem', sm: '0.875rem' }, '& input::placeholder': { color: 'rgba(215,207,207,0.9)', opacity: 1 } },
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2, background: 'rgba(188,150,94,0.05)',
                                '& fieldset': { borderColor: 'rgba(188,150,94,0.65)' },
                                '&:hover fieldset': { borderColor: 'rgba(188,150,94,0.4)' },
                                '&.Mui-focused fieldset': { borderColor: 'rgba(188,150,94,0.6)' },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Table / Empty state */}
            <Box className="glass-content-container">
                {orders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                        <EmptyIcon sx={{ fontSize: { xs: 44, sm: 56 }, color: 'rgba(188,150,94,0.25)', mb: 1.5 }} />
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.5 }}>
                            No orders yet
                        </Typography>
                        <Typography variant="body2" className="glass-text-secondary" sx={{ mb: 2.5, fontSize: { xs: '0.78rem', sm: '0.875rem' } }}>
                            Your purchased orders will appear here.
                        </Typography>
                        <Button variant="outlined" className="glass-button-outlined"
                            onClick={() => navigate('/shop')}
                            sx={{ textTransform: 'none', fontSize: '0.85rem' }}>
                            Browse products
                        </Button>
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" className="glass-text-secondary">No orders match your filter.</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} className="orders-table-container" sx={{
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            borderRadius: 2,
                            border: '1px solid rgba(188,150,94,0.15)',
                            overflowX: 'auto',
                        }}>
                            <Table size="small" className="orders-table" sx={{
                                minWidth: 620,           // ← fixed min width, table scrolls on mobile
                                tableLayout: 'fixed',
                                backgroundColor: 'transparent',
                            }}>
                                {/* Order | Date | Status | Payment | Amount | Actions */}
                                <colgroup>
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '50px' }} />
                                </colgroup>

                                <TableHead>
                                    <TableRow>
                                        <TH>#Order No.</TH>
                                        <TH>Date</TH>
                                        <TH>Status</TH>
                                        <TH>Payment</TH>
                                        <TH align="right">Amount</TH>
                                        <TH align="center">Actions</TH>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginated.map((order) => {
                                        const date = new Date(order.CreatedAt);
                                        const dateStr = date.toLocaleDateString('en-IN', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            ...(window.innerWidth > 600 && { year: 'numeric' })
                                        });
                                        const status = order.OrderStatus || 'Pending';
                                        const isNew = String(order.BillNumber) === String(newBillNumber);

                                        return (
                                            <TableRow key={order.BillId}
                                                sx={{
                                                    cursor: 'pointer',
                                                    background: isNew ? 'rgba(34,197,94,0.04)' : 'transparent',
                                                    transition: 'background 0.15s',
                                                    '&:hover': { background: 'rgba(188,150,94,0.06)' },
                                                    '&:last-child td': { borderBottom: 'none' },
                                                }}
                                                onClick={() => handleView(order)}
                                            >
                                                <TD>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#ffffffbe', fontSize: '0.82rem', letterSpacing: '0.03em' }}>
                                                            {order.BillId}
                                                        </Typography>
                                                        {isNew && (
                                                            <Chip label="New" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }} />
                                                        )}
                                                    </Box>
                                                </TD>
                                                <TD>
                                                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{dateStr}</Typography>
                                                </TD>
                                                <TD><StatusChip status={status} /></TD>
                                                {/* ── Add this payment TD ── */}
                                                <TD>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
                                                            {order.PaymentMethod === 'cod' ? '💵 COD' : '💳 Online'}
                                                        </Typography>
                                                        <Chip
                                                            label={order.PaymentStatus === 'paid' ? 'Paid' : 'Pending'}
                                                            size="small"
                                                            sx={{
                                                                height: 16, fontSize: '0.6rem', fontWeight: 700,
                                                                width: 'fit-content',
                                                                backgroundColor: order.PaymentStatus === 'paid'
                                                                    ? 'rgba(76,175,80,0.12)' : 'rgba(255,152,0,0.12)',
                                                                border: `1px solid ${order.PaymentStatus === 'paid'
                                                                    ? 'rgba(76,175,80,0.35)' : 'rgba(255,152,0,0.35)'}`,
                                                                color: order.PaymentStatus === 'paid' ? '#4caf50' : '#ff9800',
                                                            }}
                                                        />
                                                    </Box>
                                                </TD>
                                                <TD align="right">
                                                    <Typography sx={{ fontWeight: 700, color: '#ffffff', fontSize: '0.88rem', fontFamily: 'monospace' }}>
                                                        ₹{parseFloat(order.TotalAmount || 0).toFixed(2)}
                                                    </Typography>
                                                </TD>
                                                <TD align="center" sx={{ px: 1 }}>
                                                  

                                                    <Box onClick={e => e.stopPropagation()}>
                                                        <ActionsMenu order={order} onView={handleView} />
                                                    </Box>
                                                </TD>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* ── Pagination ── */}
                        <TablePagination
                            component="div"
                            count={filtered.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: '0.78rem',
                                borderTop: '1px solid rgba(188,150,94,0.1)',
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: '0.78rem',
                                    color: 'rgba(255,255,255,0.5)',
                                },
                                '.MuiTablePagination-select': {
                                    color: '#bc965e',
                                },
                                '.MuiTablePagination-actions button': {
                                    color: 'rgba(188,150,94,0.8)',
                                    '&:hover': { background: 'rgba(188,150,94,0.1)' },
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
                                },
                            }}
                        />
                    </>
                )}
            </Box>


        </Container >
    );
};

export default CustomerOrderList;